import { baseApi } from '@klp/core/api/services/rtkApi';

import { sakApiRoutes } from './api';
import {
    Sak,
    Sakskategori,
    Saksmelding as SaksmeldingClass,
    SakForsidefeed,
    SakStatusSummary as SakStatusSummaryClass,
    convertSakRequestToApiFormat,
} from './classes';
import type {
    SakRequest,
    SakResponse,
    SakStatusRequest,
    CreateSakRequest,
    SakskategoriListRequest,
    SakskategoriResponse,
    SaksmeldingResponse,
    Saksmelding,
    ISak,
    ISakskategori,
    ISaksmelding,
    INySakConfig,
    NySakConfigRequest,
} from './classes';
import type { ISakForsidefeed } from './classes/ISakForsidefeed';
import type { SakForsidefeedResponse } from './classes/SakForsidefeedResponse';
import type { SakskategoriListByTypeRequest } from './classes/sakskategoriListByTypeRequest';
import type {
    SakStatusSummary,
    ISakStatusItems,
    ISakStatusCount,
} from './classes/SakStatusSummary';

export const sakRtkApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getSakList: builder.query<SakResponse, SakRequest>({
            query: (params) => ({
                url: sakApiRoutes.sak(),
                params: {
                    kundenummerlist: params.kundeNummerList,
                    email: params.epost,
                    byggnummer: params.byggNummer,
                },
            }),
            transformResponse: (response: Array<ISak>): SakResponse => {
                const sakList = response.map((sak: ISak) =>
                    Sak.parseObject(sak),
                );
                return { sakList };
            },
            providesTags: (result, error, params) => [
                { type: 'SakList', id: params.epost || 'LIST' },
            ],
        }),
        getSak: builder.query<SakResponse, string>({
            query: (sakId) => ({
                url: sakApiRoutes.sakById(sakId),
            }),
            transformResponse: (response: ISak): SakResponse => {
                const sak = Sak.parseObject(response);
                return { sak };
            },
            providesTags: (result, error, sakId) => [
                { type: 'Sak', id: sakId },
            ],
        }),
        getSakStatusSummary: builder.query<SakStatusSummary, SakRequest>({
            queryFn: async (params, _api, _extraOptions, baseQuery) => {
                try {
                    const statusListResult = await baseQuery({
                        url: sakApiRoutes.statusItems(),
                    });

                    const countListResult = await baseQuery({
                        url: sakApiRoutes.statusCount(),
                        params: {
                            kundenummerlist: params.kundeNummerList,
                            email: params.epost,
                            byggnummer: params.byggNummer,
                        },
                    });

                    if (statusListResult.error || countListResult.error) {
                        return {
                            error:
                                statusListResult.error || countListResult.error,
                        };
                    }

                    const statusList =
                        statusListResult.data as Array<ISakStatusItems>;
                    const countList =
                        countListResult.data as Array<ISakStatusCount>;

                    const response = new SakStatusSummaryClass();

                    statusList.forEach(({ sakstatusId, navn }) => {
                        const status = countList.find(
                            (item) => item.statusId === sakstatusId,
                        );
                        if (status) {
                            response.statusSummary[navn as string] =
                                status.statusCount;
                        } else {
                            response.statusSummary[navn as string] = 0;
                        }
                    });

                    return { data: response };
                } catch (error) {
                    return { error };
                }
            },
            providesTags: (result, error, params) => [
                { type: 'SakStatusSummary', id: params.epost || 'SUMMARY' },
            ],
        }),
        getSakForsidefeed: builder.query<SakForsidefeedResponse, string>({
            query: (kundenummer) => ({
                url: sakApiRoutes.sakForsidefeed(kundenummer),
            }),
            transformResponse: (
                response: ISakForsidefeed,
            ): SakForsidefeedResponse => {
                const sakForsidefeed = SakForsidefeed.parse(response);
                return { sakForsidefeed };
            },
            providesTags: (result, error, kundenummer) => [
                { type: 'SakForsidefeed', id: kundenummer },
            ],
        }),
        getSakskategoriList: builder.query<
            SakskategoriResponse,
            SakskategoriListRequest
        >({
            query: (params) => ({
                url: sakApiRoutes.categoryItems(),
                params,
            }),
            transformResponse: (
                response: Array<ISakskategori>,
            ): SakskategoriResponse => {
                const sakskategoriList = response.map((sakskategori) =>
                    Sakskategori.parseObject(sakskategori),
                );
                return { sakskategoriList };
            },
            providesTags: [{ type: 'Sakskategori', id: 'LIST' }],
        }),
        getSakskategoriListByType: builder.query<
            SakskategoriResponse,
            SakskategoriListByTypeRequest
        >({
            query: (params) => ({
                url: sakApiRoutes.categoryItemsByType(),
                params,
            }),
            transformResponse: (
                response: Array<ISakskategori>,
            ): SakskategoriResponse => {
                const sakskategoriList = response.map((sakskategori) =>
                    Sakskategori.parseObject(sakskategori),
                );
                return { sakskategoriList };
            },
            providesTags: (result, error, params) => [
                { type: 'SakskategoriByType', id: params.type },
            ],
        }),
        updateSakStatus: builder.mutation<SakResponse, SakStatusRequest>({
            query: (statusRequest) => ({
                url: sakApiRoutes.sakStatus(
                    statusRequest.sakId,
                    statusRequest.status,
                ),
                method: 'PUT',
            }),
            transformResponse: (response: ISak): SakResponse => {
                const sak = Sak.parseObject(response);
                return { sak };
            },
            invalidatesTags: (result, error, { sakId }) => [
                { type: 'Sak', id: sakId },
                { type: 'SakList', id: 'LIST' },
                { type: 'SakStatusSummary', id: 'SUMMARY' },
            ],
        }),
        postMelding: builder.mutation<SaksmeldingResponse, Saksmelding>({
            query: (melding) => ({
                url: sakApiRoutes.sakMelding(),
                method: 'POST',
                body: melding.toSaksmelding(),
            }),
            transformResponse: (
                response: ISaksmelding,
            ): SaksmeldingResponse => {
                const saksmelding = SaksmeldingClass.parseObject(response);
                return { saksmelding };
            },
            invalidatesTags: (result, error, melding) => [
                { type: 'Sak', id: melding.sakId },
                { type: 'SakList', id: 'LIST' },
            ],
        }),
        getNySakConfig: builder.query<INySakConfig, NySakConfigRequest>({
            query: (params) => ({
                url: sakApiRoutes.nySakConfig(),
                params,
            }),
        }),
        postSak: builder.mutation<SakResponse, CreateSakRequest>({
            query: (request) => ({
                url: sakApiRoutes.sak(),
                method: 'POST',
                body: convertSakRequestToApiFormat(request),
            }),
            transformResponse: (response: ISak): SakResponse => {
                const sak = Sak.parseObject(response);
                return { sak };
            },
            invalidatesTags: [
                { type: 'SakList', id: 'LIST' },
                { type: 'SakStatusSummary', id: 'SUMMARY' },
            ],
        }),
    }),
});

export const {
    useGetSakListQuery,
    useGetSakQuery,
    useGetSakStatusSummaryQuery,
    useGetSakForsidefeedQuery,
    useGetSakskategoriListQuery,
    useGetSakskategoriListByTypeQuery,
    useUpdateSakStatusMutation,
    usePostMeldingMutation,
    usePostSakMutation,
    useGetNySakConfigQuery,
} = sakRtkApi;
